import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Refresh,
  Visibility,
  ExpandMore,
  Person,
  Security,
  Warning,
  CheckCircle,
  Error,
  Info,
  Assignment,
  AccountBalance,
  AttachMoney,
  SwapHoriz,
  Group,
  Settings,
  VpnKey,
  Block,
  Edit,
  Delete,
  Add,
  History,
  DateRange,
  AccessTime,
  Fingerprint,
  LocationOn,
  DevicesOther,
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { auditService } from '../services/api';

interface AuditLog {
  id: string;
  timestamp: string;
  user: string;
  userRole: string;
  action: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE' | 'READ' | 'APPROVE' | 'REJECT' | 'LOGIN' | 'LOGOUT';
  result: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
  ipAddress: string;
  userAgent: string;
  sessionId: string;
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: any;
  riskScore?: number;
}

interface AuditFilter {
  user?: string;
  action?: string;
  entityType?: string;
  operation?: string;
  result?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  searchText?: string;
}

const AuditTrail: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filters, setFilters] = useState<AuditFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const [timelineView, setTimelineView] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalActions: 0,
    successRate: 0,
    failedActions: 0,
    suspiciousActivities: 0,
    uniqueUsers: 0,
    highRiskActions: 0,
  });

  useEffect(() => {
    fetchAuditLogs();
    fetchStatistics();
  }, [page, rowsPerPage, filters]);

  const fetchStatistics = async () => {
    try {
      // Fetch audit statistics from backend
      const response = await auditService.getAuditLogs({
        statistics: true,
        dateFrom: filters.dateFrom?.toISOString(),
        dateTo: filters.dateTo?.toISOString(),
      });

      if (response.statistics) {
        setStats({
          totalActions: response.statistics.totalActions24h || 0,
          successRate: response.statistics.successRate || 0,
          failedActions: response.statistics.failedActions || 0,
          suspiciousActivities: response.statistics.suspiciousActivities || 0,
          uniqueUsers: response.statistics.uniqueUsers || 0,
          highRiskActions: response.statistics.highRiskActions || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch audit statistics:', error);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      // Build query parameters from filters
      const params: any = {
        page,
        size: rowsPerPage,
      };

      if (filters.user) params.user = filters.user;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.operation) params.operation = filters.operation;
      if (filters.result) params.result = filters.result;
      if (filters.searchText) params.search = filters.searchText;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom.toISOString();
      if (filters.dateTo) params.dateTo = filters.dateTo.toISOString();

      // Fetch audit logs from backend
      const response = await auditService.getAuditLogs(params);

      // Map backend response to frontend AuditLog interface
      const logs = (response.content || response.data || response).map((log: any) => ({
        id: log.id,
        timestamp: log.timestamp || log.createdAt,
        user: log.user || log.username || log.performedBy,
        userRole: log.userRole || log.role || 'UNKNOWN',
        action: log.action || log.description,
        entityType: log.entityType,
        entityId: log.entityId,
        entityName: log.entityName,
        operation: log.operation || log.actionType,
        result: log.result || log.status || 'SUCCESS',
        ipAddress: log.ipAddress || log.ip || 'N/A',
        userAgent: log.userAgent || log.browser || 'N/A',
        sessionId: log.sessionId || log.session || 'N/A',
        changes: log.changes || log.modifications,
        metadata: log.metadata || log.additionalInfo,
        riskScore: log.riskScore || log.riskLevel,
      }));

      setAuditLogs(logs);
      setTotalCount(response.totalElements || response.total || logs.length);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof AuditFilter, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const exportAuditLogs = () => {
    // Export logic here
    alert('Exporting audit logs...');
  };

  const getOperationIcon = (operation: AuditLog['operation']) => {
    switch (operation) {
      case 'CREATE': return <Add color="success" />;
      case 'UPDATE': return <Edit color="info" />;
      case 'DELETE': return <Delete color="error" />;
      case 'READ': return <Visibility color="action" />;
      case 'APPROVE': return <CheckCircle color="success" />;
      case 'REJECT': return <Block color="error" />;
      case 'LOGIN': return <VpnKey color="primary" />;
      case 'LOGOUT': return <VpnKey color="action" />;
      default: return <Assignment />;
    }
  };

  const getResultColor = (result: AuditLog['result']) => {
    switch (result) {
      case 'SUCCESS': return 'success';
      case 'FAILURE': return 'error';
      case 'PARTIAL': return 'warning';
      default: return 'default';
    }
  };

  const getRiskBadge = (score?: number) => {
    if (!score) return null;

    const color = score >= 8 ? 'error' : score >= 5 ? 'warning' : 'success';
    const label = score >= 8 ? 'High Risk' : score >= 5 ? 'Medium Risk' : 'Low Risk';

    return <Chip label={label} color={color} size="small" />;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Audit Trail</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setTimelineView(!timelineView)}
            >
              {timelineView ? 'Table View' : 'Timeline View'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportAuditLogs}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchAuditLogs}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Total Actions (24h)
                </Typography>
                <Typography variant="h5">
                  {stats.totalActions.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Success Rate
                </Typography>
                <Typography variant="h5" color="success.main">
                  {stats.successRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Failed Actions
                </Typography>
                <Typography variant="h5" color="error.main">
                  {stats.failedActions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Suspicious
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {stats.suspiciousActivities}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Unique Users
                </Typography>
                <Typography variant="h5">
                  {stats.uniqueUsers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  High Risk
                </Typography>
                <Typography variant="h5" color="error.main">
                  {stats.highRiskActions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ mb: 3, p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterList />
            </IconButton>
          </Box>
          {showFilters && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.searchText || ''}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="User"
                  value={filters.user || ''}
                  onChange={(e) => handleFilterChange('user', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={filters.entityType || ''}
                    label="Entity Type"
                    onChange={(e) => handleFilterChange('entityType', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="VOUCHER">Voucher</MenuItem>
                    <MenuItem value="PARTNER">Partner</MenuItem>
                    <MenuItem value="SETTLEMENT">Settlement</MenuItem>
                    <MenuItem value="PAYMENT">Payment</MenuItem>
                    <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Operation</InputLabel>
                  <Select
                    value={filters.operation || ''}
                    label="Operation"
                    onChange={(e) => handleFilterChange('operation', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CREATE">Create</MenuItem>
                    <MenuItem value="UPDATE">Update</MenuItem>
                    <MenuItem value="DELETE">Delete</MenuItem>
                    <MenuItem value="READ">Read</MenuItem>
                    <MenuItem value="APPROVE">Approve</MenuItem>
                    <MenuItem value="REJECT">Reject</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Result</InputLabel>
                  <Select
                    value={filters.result || ''}
                    label="Result"
                    onChange={(e) => handleFilterChange('result', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="FAILURE">Failure</MenuItem>
                    <MenuItem value="PARTIAL">Partial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From Date"
                  value={filters.dateFrom || null}
                  onChange={(date) => handleFilterChange('dateFrom', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={filters.dateTo || null}
                  onChange={(date) => handleFilterChange('dateTo', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchAuditLogs}
                    startIcon={<Search />}
                  >
                    Search
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Audit Logs Display */}
        {timelineView ? (
          <Paper sx={{ p: 3 }}>
            <Timeline position="alternate">
              {auditLogs.map((log, index) => (
                <TimelineItem key={log.id}>
                  <TimelineOppositeContent>
                    <Typography color="text.secondary">
                      {format(parseISO(log.timestamp), 'PPp')}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color={getResultColor(log.result) as any}>
                      {getOperationIcon(log.operation)}
                    </TimelineDot>
                    {index < auditLogs.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Paper elevation={3} sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="h6" component="span">
                            {log.action}
                          </Typography>
                          {getRiskBadge(log.riskScore)}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {log.user} ({log.userRole})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.entityType} - {log.entityId}
                      </Typography>
                      {log.changes && (
                        <Box mt={1}>
                          {log.changes.map((change, idx) => (
                            <Typography key={idx} variant="caption" display="block">
                              {change.field}: {String(change.oldValue)} → {String(change.newValue)}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Paper>
        ) : (
          <Paper>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Operation</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="caption">
                          {format(parseISO(log.timestamp), 'MMM dd, HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24 }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{log.user}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {log.userRole}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.action}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.entityType}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {log.entityId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getOperationIcon(log.operation)}
                          <Typography variant="body2">
                            {log.operation}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.result}
                          color={getResultColor(log.result) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.userAgent}>
                          <Typography variant="caption">
                            {log.ipAddress}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {getRiskBadge(log.riskScore)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedLog(log);
                              setDetailsOpen(true);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}

        {/* Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Audit Log Details
            {selectedLog && getRiskBadge(selectedLog.riskScore)}
          </DialogTitle>
          <DialogContent>
            {selectedLog && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Timestamp
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(parseISO(selectedLog.timestamp), 'PPpp')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    User
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.user} ({selectedLog.userRole})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Action
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.action}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Entity Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.entityType}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Entity ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.entityId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Operation
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getOperationIcon(selectedLog.operation)}
                    <Typography variant="body1">
                      {selectedLog.operation}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Result
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedLog.result}
                      color={getResultColor(selectedLog.result) as any}
                      size="small"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.ipAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Session ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.sessionId}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    User Agent
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {selectedLog.userAgent}
                  </Typography>
                </Grid>

                {selectedLog.changes && selectedLog.changes.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Changes
                      </Typography>
                      <List>
                        {selectedLog.changes.map((change, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={change.field}
                              secondary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="caption" component="span">
                                    {String(change.oldValue)}
                                  </Typography>
                                  <Typography variant="caption" component="span">
                                    →
                                  </Typography>
                                  <Typography variant="caption" component="span" color="primary">
                                    {String(change.newValue)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </>
                )}

                {selectedLog.metadata && (
                  <>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Additional Information
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(selectedLog.metadata).map(([key, value]) => (
                          <Grid item xs={12} sm={6} key={key}>
                            <Typography variant="caption" color="textSecondary">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </Typography>
                            <Typography variant="body2">
                              {String(value)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AuditTrail;